export type TelemetryEventType =
  | 'webrtc.connected'
  | 'webrtc.failed'
  | 'webrtc.disconnected'
  | 'rpc.succeeded'
  | 'rpc.failed'
  | 'rpc.timeout'
  | 'websocket.reconnected'
  | 'tts.failed';

export type TelemetryEvent = {
  eventType: TelemetryEventType;
  robotId?: string;
  connectionSessionId?: string;
  flowRunId?: string;
  nodeId?: string;
  requestId?: string;
  durationMs?: number;
  errorCode?: string;
};

export type TelemetryReporter = (event: TelemetryEvent) => void | Promise<void>;

const identifier = (value: string | undefined) => value ? value.slice(0, 128) : undefined;
const errorCode = (value: string | undefined) => value && /^[A-Z][A-Z0-9_]{0,63}$/.test(value) ? value : undefined;

export const sanitizeTelemetryEvent = (event: TelemetryEvent): TelemetryEvent => ({
  eventType: event.eventType,
  ...(identifier(event.robotId) ? { robotId: identifier(event.robotId) } : {}),
  ...(identifier(event.connectionSessionId) ? { connectionSessionId: identifier(event.connectionSessionId) } : {}),
  ...(identifier(event.flowRunId) ? { flowRunId: identifier(event.flowRunId) } : {}),
  ...(identifier(event.nodeId) ? { nodeId: identifier(event.nodeId) } : {}),
  ...(identifier(event.requestId) ? { requestId: identifier(event.requestId) } : {}),
  ...(Number.isFinite(event.durationMs) ? { durationMs: Math.min(300_000, Math.max(0, Math.round(event.durationMs!))) } : {}),
  ...(errorCode(event.errorCode) ? { errorCode: errorCode(event.errorCode) } : {}),
});

export const reportTelemetrySafely = (reporter: TelemetryReporter, event: TelemetryEvent) => {
  try {
    void Promise.resolve(reporter(sanitizeTelemetryEvent(event))).catch(() => undefined);
  } catch {
    // Telemetry must never interrupt the user operation that produced it.
  }
};

export const NOOP_TELEMETRY_REPORTER: TelemetryReporter = () => undefined;
